// src/context/AuthContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

const AuthContext = createContext({
  user: null,
  setUser: () => {},
  loadingUser: false,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }) {
  // init user from localStorage so UI can show immediately if cached
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // loading flag while we confirm / fetch user from API if token exists
  const [loadingUser, setLoadingUser] = useState(() => !!localStorage.getItem("token"));

  // Helper to normalize API response shapes
  const _extractToken = (data) =>
    data?.token || data?.access_token || data?.data?.token || null;
  const _extractUser = (data) =>
    data?.user || data?.data?.user || (data && typeof data === "object" && data.id ? data : null);

  // refreshUser: fetch /api/user when we have a token
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setLoadingUser(false);
      return null;
    }

    setLoadingUser(true);
    try {
      const res = await fetch(`${API_BASE}/api/user`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });

      if (!res.ok) {
        // token invalid/expired - clear
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
        setLoadingUser(false);
        return null;
      }

      const data = await res.json();
      const userData = _extractUser(data) || data;
      setUser(userData);
      try {
        localStorage.setItem("user", JSON.stringify(userData));
      } catch {}
      setLoadingUser(false);
      return userData;
    } catch (err) {
      console.error("refreshUser error", err);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      setLoadingUser(false);
      return null;
    }
  }, []);

  // run once on mount: refresh if token present
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // login: accept credentials { email?, phone?, password }
  // returns API response (parsed json) or throws
  const login = useCallback(async (credentials) => {
    setLoadingUser(true);
    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(credentials),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.message || (data.errors ? JSON.stringify(data.errors) : "خطا در ورود");
        throw new Error(msg);
      }

      // extract token & user if returned
      const token = _extractToken(data);
      const userData = _extractUser(data);

      if (token) {
        try {
          localStorage.setItem("token", token);
        } catch {}
      }

      if (userData) {
        setUser(userData);
        try {
          localStorage.setItem("user", JSON.stringify(userData));
        } catch {}
      } else {
        // fallback: refresh user from /api/user using token
        await refreshUser();
      }

      setLoadingUser(false);
      return data;
    } catch (err) {
      setLoadingUser(false);
      throw err;
    }
  }, [refreshUser]);

  // register: accepts payload for /api/register and behaves like login on success
  const register = useCallback(async (payload) => {
    setLoadingUser(true);
    try {
      const res = await fetch(`${API_BASE}/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.message || (data.errors ? JSON.stringify(data.errors) : "خطا در ثبت‌نام");
        throw new Error(msg);
      }

      const token = _extractToken(data);
      const userData = _extractUser(data);

      if (token) {
        try {
          localStorage.setItem("token", token);
        } catch {}
      }

      if (userData) {
        setUser(userData);
        try {
          localStorage.setItem("user", JSON.stringify(userData));
        } catch {}
      } else {
        await refreshUser();
      }

      setLoadingUser(false);
      return data;
    } catch (err) {
      setLoadingUser(false);
      throw err;
    }
  }, [refreshUser]);

  // logout: best-effort API call, then clear everything
  const logout = useCallback(async () => {
    const token = localStorage.getItem("token");
    try {
      if (token) {
        await fetch(`${API_BASE}/api/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
      }
    } catch (err) {
      console.warn("logout API failed", err);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      setLoadingUser(false);
    }
  }, []);

  // expose API
  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loadingUser,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// convenient hook
export function useAuth() {
  return useContext(AuthContext);
}
